/**
 * Entry: the gateway after the loader. Keeps the original rhythm (one
 * chapter forward, the others peeking from behind; tap a side chapter to
 * bring it forward, tap the front one to go in, swipe on touch) but
 * presented as a quiet editorial index: large type, one clear action.
 * Photos are grey until their chapter is in front, then come to colour.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { CHAPTERS } from "../data/chapters";
import { img } from "../data/images";
import { useV5 } from "../lib/context";
import Button from "../components/Button";

const N = CHAPTERS.length;
const rel = (i, a) => ((i - a + N + Math.floor(N / 2)) % N) - Math.floor(N / 2);

export default function Entry() {
  const { go, ready } = useV5();
  const [active, setActive] = useState(0);
  const touch = useRef(null);

  const step = useCallback((d) => setActive((a) => (a + d + N) % N), []);
  const enter = useCallback((c) => (c.external ? window.open(c.href, "_blank", "noopener") : go(c.to, c.label)), [go]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "Enter") enter(CHAPTERS[active]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, step, enter]);

  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const s = touch.current; touch.current = null; if (!s) return;
    const t = e.changedTouches[0], dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
    step(dx < 0 ? 1 : -1);
  };

  const c = CHAPTERS[active];

  return (
    <section className={`entry${ready ? " is-ready" : ""}`}>
      <div className="entry-top t-cap">
        <span className="entry-brand">Digital Billions Group</span>
        <span>Select a chapter</span>
      </div>

      <div className="entry-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} role="group" aria-label="Chapters">
        {CHAPTERS.map((ch, i) => {
          const r = rel(i, active);
          const on = r === 0;
          const image = img(ch.image);
          return (
            <button
              key={ch.id}
              type="button"
              className={`entry-tile${on ? " is-active" : ""}`}
              style={{ "--r": r, "--abs": Math.abs(r), pointerEvents: Math.abs(r) > 1 ? "none" : "auto" }}
              tabIndex={Math.abs(r) > 1 ? -1 : 0}
              aria-current={on ? "true" : undefined}
              aria-label={on ? `Enter ${ch.label}` : `Bring ${ch.label} forward`}
              onClick={() => (on ? enter(ch) : setActive(i))}>
              {image.video && Math.abs(r) <= 1
                ? <video src={image.video} poster={image.src} autoPlay loop muted playsInline preload="metadata" aria-hidden="true" />
                : <img src={image.src} alt="" width={image.w} height={image.h} draggable="false" />}
              <span className="entry-tile-num t-cap">{ch.num}</span>
            </button>
          );
        })}
      </div>

      <div className="entry-info">
        <div className="entry-title-col">
          <span className="t-cap entry-count">{c.num} / {String(N).padStart(2, "0")}</span>
          <h1 className="entry-title" key={c.id} aria-live="polite">
            <span className="mask"><span className="rise">{c.label}</span></span>
          </h1>
        </div>
        <div className="entry-text-col" key={`${c.id}-t`}>
          <p className="t-lg entry-blurb">{c.blurb}</p>
          <Button variant="primary" onClick={() => enter(c)} label={c.label}>{c.cta}</Button>
        </div>
      </div>

      <div className="entry-hint t-cap">
        <span className="hint-touch">Swipe to browse</span>
        <span className="hint-key">← → to browse · Enter to open</span>
      </div>
    </section>
  );
}
